import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import OutsideClickHandler from 'react-outside-click-handler';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck, faPlus } from '@fortawesome/pro-regular-svg-icons';
import cn from 'classnames';
import './ToggleUserTypeSwitch.scss';

import { setUIKeyValue } from '../../../Actions/UIActions';
import { isShowingShopperHeaderToCreator } from '../../../Helpers/ui_helpers';
import { getUserTier, isBrand, isShopper, isLoggedIn, getName } from '../../../Helpers/user_helpers';
import { getFirstName } from '../../../Helpers/formatting';
import { getUserTierDisplay, getUserTierExplainer, getTierMeterData } from '../../../Helpers/tier_helpers';

import ProfilePhoto from '../../Profile/ProfilePhoto';

const ToggleUserTypeSwitch = props => {
  const { user, ui } = props;

  const showingShopperHeader = isShopper(user) || isShowingShopperHeaderToCreator(ui);

  // UI Handling
  const [isShowingOptionsPanel, setIsShowingOptionsPanel] = React.useState(false);
  const openOptionsPanel = () => {
    window.__ADD_EVENT__('Navigation - Open User Type Options Panel');
    setIsShowingOptionsPanel(true);
  };
  const closeOptionsPanel = () => setIsShowingOptionsPanel(false);
  const closeOptionsPanelWithSmallDelay = () => setTimeout(() => setIsShowingOptionsPanel(false), 50); // Use for outside click handler

  // Tier Data
  const tierObj = getUserTier(user);
  const tierValues = getTierMeterData(user);
  const tierLabel = tierObj ? getUserTierDisplay(tierObj.tier) : 'None';
  const tierExplainer = tierObj ? getUserTierExplainer(tierObj.tier) : 'None';

  // Do not show if not logged in or brand account
  if (!isLoggedIn(user)) return null;
  if (isBrand(user)) return null;

  const creatorAccount = {
    type: 'creator',
    isActive: !showingShopperHeader,
    label: isShopper(user) ? 'Apply For Creator Account' : getName(user),
    sublabel: isShopper(user) ? 'Monetize Your Influence' : 'Creator',
    hrefInsteadOfOnClick: isShopper(user) ? '/apply/creator' : null,
    switchLabel: isShopper(user) ? null : 'Switch to Creator',
    imageEl: isShopper(user) ? (
      <div className='apply-add-icon'>
        <FontAwesomeIcon icon={faPlus} />
      </div>
    ) : (
      <ProfilePhoto ui={ui} profile={user.profile} basic={true} />
    )
  };

  const shopperAccount = {
    type: 'shopper',
    label: getName(user),
    isActive: showingShopperHeader,
    sublabel: 'Shopper',
    switchLabel: 'Switch to Shopper',
    imageEl: <ProfilePhoto ui={ui} profile={user.profile} basic={true} />
  };

  const accounts = isShopper(user)
    ? [shopperAccount, creatorAccount]
    : window.__NEW_SHOP_MODE__
    ? [creatorAccount, shopperAccount]
    : [creatorAccount];

  return (
    <div className='toggle-user-type-switch-container hide-on-searching'>
      <div onClick={isShowingOptionsPanel ? closeOptionsPanel : openOptionsPanel} className='switch-inner'>
        {showingShopperHeader ? (
          <div className={cn('switch-content', { shopper: showingShopperHeader })}>
            <div className='label'>{isShopper(user) ? getFirstName(getName(user)) : 'Shopper'}</div>
            <ProfilePhoto ui={ui} profile={user.profile} basic={true} />
          </div>
        ) : (
          <div className='switch-content'>
            <ProfilePhoto ui={ui} profile={user.profile} basic={true} />
            {!isShopper(user) && (
              <div className='score-container'>
                <div className='label'>{tierLabel}</div>
              </div>
            )}
          </div>
        )}
      </div>
      {isShowingOptionsPanel && (
        <OutsideClickHandler onOutsideClick={closeOptionsPanelWithSmallDelay}>
          <div className='options-panel'>
            <div onClick={closeOptionsPanel} className='close-icon'>
              <FontAwesomeIcon icon={faTimes} />
            </div>
            <div className='tier-container'>
              <div className='tier-main'>
                <div className='tier-label'>{isShopper(user) ? 'Shopper Account' : tierLabel}</div>
                <div className='tier-desc'>{isShopper(user) ? "Here to discover things I'll love." : tierExplainer}</div>
              </div>
              {!isShopper(user) && (
                <div className='tier-secondary'>
                  <div className='points'>
                    {tierValues.pointsInt}/100 <span className='points-label'>points</span>
                  </div>
                </div>
              )}
            </div>
            {!isShopper(user) && (
              <div className='tier-meter-outer'>
                <div className='tier-meter'>
                  <div className='tier-meter-active' style={{ width: `${tierValues.pointsInt}%` }} />
                </div>
                <div className='tier-meter-info'>
                  {tierValues.nextTier ? (
                    <div className='tier-meter-info-points'>
                      <span className='points-number'>{tierValues.pointsUntilNextTier}</span> point{tierValues.pointsUntilNextTier === 1 ? '' : 's'}{' '}
                      until {tierValues.nextTier} tier
                    </div>
                  ) : (
                    <div />
                  )}
                  <Link onClick={closeOptionsPanel} to='/settings?tab=Creator+Tier' className='learn-more'>
                    See Tier Details
                  </Link>
                </div>
              </div>
            )}
            <div className='accounts'>
              {accounts.map(account => {
                const toggleType = () => {
                  if (account.isActive) return;
                  props.setUIKeyValue('headerType', account.type);
                  window.__ADD_EVENT__('Navigation - Switch User Type', { userType: account.type });
                  account.type === 'shopper'
                    ? window.ALERT.success(`Switched to Shopper Account`, { hideAfter: 1.25 })
                    : window.ALERT.success(`Switched to Creator Account`, { hideAfter: 1.25 });
                  closeOptionsPanel();
                };
                const onClick = account.hrefInsteadOfOnClick ? () => (window.location.href = account.hrefInsteadOfOnClick) : toggleType;
                return (
                  <div onClick={onClick} key={account.type} className={cn('account', { active: account.isActive })}>
                    {account.imageEl}
                    <div className='account-info'>
                      <div className='label'>{account.label}</div>
                      <div className='sublabel'>{account.sublabel}</div>
                    </div>
                    <div className='indicator'>
                      {account.isActive ? (
                        <div className='check-icon'>
                          <FontAwesomeIcon icon={faCheck} />
                        </div>
                      ) : (
                        account.switchLabel && <div className='switch-label'>{account.switchLabel}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className='footer'>
              <Link onClick={closeOptionsPanel} to='/settings' className='footer-button'>
                Go to Account Settings
              </Link>
            </div>
          </div>
        </OutsideClickHandler>
      )}
    </div>
  );
};

ToggleUserTypeSwitch.propTypes = {
  user: PropTypes.object.isRequired,
  ui: PropTypes.object.isRequired,
  setUIKeyValue: PropTypes.func.isRequired
};

const mapStateToProps = state => {
  const { user, ui } = state;
  return { user, ui };
};

export default connect(mapStateToProps, {
  setUIKeyValue
})(ToggleUserTypeSwitch);
